/**
 * Collapses a unit's selected periods into a compact human range.
 *
 * The raw selection is one label per picked "klocek", which for a long
 * campaign renders as an unreadable strip
 * ("16–31 maj, 1–15 cze, 16–30 cze, 1–15 lip, 16–31 lip"). Contiguous runs
 * carry no extra information once collapsed, so this folds each run into a
 * single span ("16 maj – 31 lip") and only keeps a comma between genuine
 * gaps ("1–15 kwi, 16 maj – 31 lip").
 *
 * `periods` MUST be in chronological order — adjacency is derived from array
 * position, which is how the period strip itself is built
 * (`periodsFor(granularity)`).
 */

/** Half-month/week labels look like "16–31 maj"; month labels are bare ("kwi"). */
const RANGE_LABEL = /^(\d+)–(\d+)\s+(.+)$/;

/**
 * `ClientOfferReviewPeriod.title` is the type's documented "day granularity
 * only" field: the DAY strip is too narrow for more than a bare day number, so
 * the readable "{day} {month}" goes in `title` while `label` holds just the day.
 * Reading `label` alone would render a monthless "16 - 20".
 *
 * Only the mockup's `periodsFor('DAY')` populates `title` today. Production's
 * `buildReviewPeriods` (build-review-periods.ts) always emits a
 * month-bearing `label` regardless of granularity and never sets `title`, so
 * this falls through to `label` there. The fallback stays because it honours
 * the shared type contract and is the live path for the mockup, which imports
 * this helper directly.
 */
function displayLabel(period: { label: string; title?: string }): string {
  return period.title ?? period.label;
}

interface PeriodRun {
  from: string;
  to: string;
}

function formatRun({ from, to }: PeriodRun): string {
  if (from === to) return from;
  const start = RANGE_LABEL.exec(from);
  const end = RANGE_LABEL.exec(to);
  // "16–31 maj" + "16–31 lip" -> "16 maj – 31 lip". Falls back to joining the
  // whole labels for granularities whose label is not a day range (MONTH).
  if (start && end) return `${start[1]} ${start[3]} – ${end[2]} ${end[3]}`;
  return `${from} – ${to}`;
}

export function summarizeSelectedPeriods(
  periods: readonly { id: string; label: string; title?: string }[],
  selectedIds: readonly string[],
): string {
  const selected = new Set(selectedIds);
  const runs: PeriodRun[] = [];
  let current: PeriodRun | null = null;

  for (const period of periods) {
    if (selected.has(period.id)) {
      const label = displayLabel(period);
      if (current) current.to = label;
      else current = { from: label, to: label };
      continue;
    }
    if (current) {
      runs.push(current);
      current = null;
    }
  }
  if (current) runs.push(current);

  return runs.map(formatRun).join(', ');
}
